import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import * as PropertyTypes from 'constants/propertyType';
import { DateTime } from '@ts-private/rocket-extensions';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_DOCUMENT_TYPE_PROPERTIES_WRITE } from 'constants/rightsCodes';

export const DocumentTypePropertyGeneralDataComponent = ({
  documentTypeXProperty,
  onPropertyChanged,
  properties,
  propertyTypes,
  propertyDetails,
  validation
}) => {
  const [t] = useTranslation();
  const canWrite = usePermission(ADMIN_DOCUMENT_TYPE_PROPERTIES_WRITE);
  const propertyTypeName = useMemo(
    () => propertyTypes.find(x => x.dictionaryItemId === documentTypeXProperty?.propertyTypeId)?.dictionaryItemName || '',
    [documentTypeXProperty?.propertyTypeId, propertyTypes]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          valueKey='propertyId'
          value={{ propertyId: documentTypeXProperty.propertyId, propertyName: documentTypeXProperty.propertyName }}
          options={properties}
          labelKey='propertyName'
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.PropertyName')}
          onChange={event => {
            onPropertyChanged('propertyId', event !== null ? event.propertyId : null);
            onPropertyChanged('propertyName', event !== null ? event.propertyName : null);
            onPropertyChanged('propertyCode', event !== null ? event.propertyCode : null);
            onPropertyChanged('propertyTypeId', event !== null ? event.propertyTypeId : null);
            onPropertyChanged('defaultValue', null);
            onPropertyChanged('orderNo', event !== null ? event.orderNo : null);
          }}
          error={!isValid(validation.propertyId)}
          helperText={getErrors(validation.propertyId)}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={documentTypeXProperty?.propertyCode || ''}
          disabled
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.PropertyCode')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          value={propertyTypeName}
          disabled
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.PropertyType')}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        {propertyDetails && propertyDetails.length > 0 ? (
          <Autocomplete
            fullWidth
            valueKey='propertyValue'
            value={documentTypeXProperty.defaultValue}
            simpleValue
            options={propertyDetails}
            labelKey='propertyValue'
            label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.DefaultValue')}
            onChange={event => onPropertyChanged('defaultValue', event)}
            isSearchable={true}
            disabled={!canWrite}
          />
        ) : (
          <>
            {documentTypeXProperty?.propertyTypeId === PropertyTypes.textPropertyType && (
              <TextField
                fullWidth
                label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.DefaultValue')}
                value={documentTypeXProperty.defaultValue || ''}
                onChange={event => onPropertyChanged('defaultValue', event)}
                disabled={!canWrite}
              />
            )}
            {documentTypeXProperty?.propertyTypeId === PropertyTypes.numericPropertyType && (
              <TextField
                isNumeric
                fullWidth
                label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.DefaultValue')}
                value={documentTypeXProperty.defaultValue || ''}
                onChange={event => onPropertyChanged('defaultValue', event)}
                disabled={!canWrite}
              />
            )}
            {documentTypeXProperty?.propertyTypeId === PropertyTypes.dataPropertyType && (
              <DateTime
                name='validFrom'
                value={documentTypeXProperty.defaultValue}
                timeFormat={false}
                label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.DefaultValue')}
                onChange={event => onPropertyChanged('defaultValue', event)}
                disabled={!canWrite}
              />
            )}
          </>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          isNumeric
          fullWidth
          value={documentTypeXProperty.orderNo || ''}
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.OrderNo')}
          decimalScale={0}
          onChange={event => onPropertyChanged('orderNo', event)}
          error={!isValid(validation.orderNo)}
          helperText={getErrors(validation.orderNo)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={6} sm={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(documentTypeXProperty.implicit) || false}
              onChange={event => onPropertyChanged('implicit', event.target.checked)}
              color='primary'
              disabled={!canWrite}
            />
          }
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.Implicit')}
        />
      </Grid>
      <Grid item xs={6} sm={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(documentTypeXProperty.mandatory) || false}
              onChange={event => onPropertyChanged('mandatory', event.target.checked)}
              color='primary'
              disabled={!canWrite}
            />
          }
          label={t('DocumentTypes.DocumentTypeProperty.EditDocumentTypeProperty.Mandatory')}
        />
      </Grid>
    </Grid>
  );
};

DocumentTypePropertyGeneralDataComponent.propTypes = {
  documentTypeXProperty: PropTypes.object,
  onPropertyChanged: PropTypes.func.isRequired,
  properties: PropTypes.array.isRequired,
  propertyTypes: PropTypes.array.isRequired,
  propertyDetails: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired
};

export default DocumentTypePropertyGeneralDataComponent;
